import type { IVendorAssessmentFormData } from "@/context/data.types";
import {
  isProduceRacVendorAssessmentLayout,
  resolveProductSpecificationTypeFromApiSources,
} from "@/utils/productSpecificationTypeUtils";
export {
  resolveProductSpecificationType,
  resolveProductSpecificationTypeFromApiSources,
} from "@/utils/productSpecificationTypeUtils";

/** Parse comma-separated country IDs from form state or API. */
export function parseCountryOfOriginIds(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const o = item as Record<string, unknown>;
          return String(o.id ?? o.value ?? "").trim();
        }
        return String(item).trim();
      })
      .filter(Boolean);
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const id = o.id ?? o.value;
    return id != null && String(id).trim() !== "" ? [String(id).trim()] : [];
  }
  return String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Normalize API countryOfOrigin to comma-separated ID string for form state. */
export function normalizeCountryOfOriginFromApi(raw: unknown): string {
  return parseCountryOfOriginIds(raw).join(",");
}

/** Format selected country IDs for save payload (comma-separated). */
export function formatCountryOfOriginForSave(ids: string[]): string {
  return ids.map((id) => id.trim()).filter(Boolean).join(",");
}

export const METAL_DETECTION_XRAY_OTHER_VALUE = "Other";

export function isMetalDetectionXrayOtherSelected(
  value?: string | null
): boolean {
  return String(value ?? "").trim() === METAL_DETECTION_XRAY_OTHER_VALUE;
}

export function formatMetalDetectionXrayFieldsForSave(
  form: Pick<
    IVendorAssessmentFormData,
    "metalDetectionXrayOnProductLine" | "otherMetalDetectionXrayOnProductLine"
  >
): Pick<
  IVendorAssessmentFormData,
  "metalDetectionXrayOnProductLine" | "otherMetalDetectionXrayOnProductLine"
> {
  return {
    metalDetectionXrayOnProductLine: form.metalDetectionXrayOnProductLine,
    otherMetalDetectionXrayOnProductLine: isMetalDetectionXrayOtherSelected(
      form.metalDetectionXrayOnProductLine
    )
      ? form.otherMetalDetectionXrayOnProductLine
      : "",
  };
}

/** Shared VA fields for Produce RAC layouts (datapoint common set). */
export const PRODUCE_RAC_COMMON_VA_FIELDS: ReadonlySet<
  keyof IVendorAssessmentFormData
> = new Set([
  "vendorName",
  "productName",
  "productDeveloper",
  "typeOfVendor",
  "addedOn",
  "countryOfOrigin",
  "canTrackLotCodes",
  "prop65Warning",
  "privateLabelFosUsCompanies",
  "capacityProductionLines",
  "capacityFacilities",
  "manufacturingFacilityLocations",
  "facilityGfsiCertified",
  "certificationWithdrawnWithinLast5Years",
  "haccpFspPlan",
  "testFinishedProductsForPathogens",
  "metalDetectionXrayOnProductLine",
  "otherMetalDetectionXrayOnProductLine",
  "allergenManagementProgram",
  "productRecallProcedure",
  "recallWithinLast5Years",
  "recallDetail",
  "regulatoryEnforcementAction",
  "adulteratedProductPoliciesProcedures",
  "importAutomaticDetention",
  "fsma",
  "edi",
]);

type FieldPermissionSlice = { isHidden?: boolean } | undefined;

/**
 * When spec type is Produce RAC (TJ Label or Branded), only RAC fields are shown.
 * Otherwise existing F&B / default behavior (all fields except permission.isHidden).
 */
export function shouldShowVendorAssessmentField(
  field: keyof IVendorAssessmentFormData,
  productSpecificationType?: string | null,
  fieldPermission?: FieldPermissionSlice
): boolean {
  if (fieldPermission?.isHidden) {
    return false;
  }

  if (!isProduceRacVendorAssessmentLayout(productSpecificationType)) {
    return true;
  }

  return PRODUCE_RAC_COMMON_VA_FIELDS.has(field);
}

/** Resolves productSpecificationType from one or more API string sources. */
export function resolveVendorAssessmentProductSpecificationType(
  ...apiSources: (string | null | undefined)[]
): string {
  return resolveProductSpecificationTypeFromApiSources(...apiSources);
}
